import React, { useState } from "react";
import {Button, CopyToClipboard, Modal, SpaceBetween} from "@amzn/awsui-components-react";
import { getQuery } from "../../helpers/logUtils";
import JsonCompare from "./JsonCompare";
import { AccountingEventsGroup, CanonicalBusinessEvent } from "../../types";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../../types/documents";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
  isDigitalBusinessDocument,
} from "../../helpers/typeChecks";
import ViewLogs from "./ViewLogs";
import {sortedStringify} from "../../helpers/jsonUtils";

interface LogButtonsProps {
  identifier: string;
  stage: string;
  logGroups: string[];
  dateRange: [Date, Date];
  isDlq?: boolean;
  data?: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | CanonicalBusinessEvent
  )[];
}

const EventButtons: React.FC<LogButtonsProps> = ({
  identifier,
  stage,
  logGroups,
  dateRange,
  isDlq = false,
  data,
}) => {
  const logQuery = getQuery(identifier, isDlq);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleModalOpen = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  function getId(): string {
    if (!data?.length) return "";

    const firstItem = data[0];
    switch (true) {
      case isAccountingEventsGroup(firstItem):
        return (firstItem as AccountingEventsGroup).pk ?? "";
      case isDigitalBusinessDocument(firstItem):
        return (firstItem as DigitalBusinessDocument).id ?? "";
      case isCanonicalBusinessEvent(firstItem):
        return (firstItem as CanonicalBusinessEvent).pk ?? "";
      default:
        return "";
    }
  }

  // Only show the Compare JSON button if data exists and has items
  const hasComparableData = data && data.length > 0;

  return (
    <SpaceBetween alignItems="center" direction="horizontal" size="xxs">
      <ViewLogs
        stage={stage}
        logGroups={logGroups}
        logQuery={logQuery}
        dateRange={dateRange}
      />
      {hasComparableData && (
        <>
          <Button onClick={handleModalOpen}>Compare JSON</Button>
          <Modal
            visible={isModalVisible}
            onDismiss={handleModalClose}
            header={`JSON Comparison - ${getId()}`}
            size="max"
          >
            <JsonCompare isModalVisible={isModalVisible} objects={data} />
          </Modal>
        </>
      )}
      <CopyToClipboard
          copyButtonText="Copy JSON"
          copySuccessText="Copied to clipboard"
          copyErrorText="Error copying"
          textToCopy={sortedStringify(data?.at(-1), 2)}
      />
    </SpaceBetween>
  );
};

export default EventButtons;
